/**
 * Builds a readable message for a failed file upload. The reply is not always JSON (a proxy
 * in front of the server can answer with an HTML page, e.g. 413 "Request Entity Too Large"),
 * so it is read as text first and the HTTP status is used as the fallback.
 */
export async function describeUploadFailure(res: Response, fileName: string): Promise<string> {
  const body = await res.text().catch(() => '');
  let serverMessage = '';
  try {
    serverMessage = JSON.parse(body).error || '';
  } catch {
    // not JSON; use the status below
  }
  console.error('File upload failed:', res.status, body.slice(0, 500));

  if (serverMessage) return `Could not upload "${fileName}": ${serverMessage}`;
  if (res.status === 413) {
    return `Could not upload "${fileName}": the file is larger than the server or its proxy allows (HTTP 413).`;
  }
  return `Could not upload "${fileName}" (server responded ${res.status}${res.statusText ? ` ${res.statusText}` : ''}).`;
}

/** The server refuses files above this size (see file.routes.ts); the check is done here first so the person is told at once */
export const MAX_UPLOAD_MB = 100;
export const MAX_UPLOAD_BYTES = MAX_UPLOAD_MB * 1024 * 1024;

export function formatFileSize(bytes: number): string {
  if (bytes >= 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  if (bytes >= 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${bytes} B`;
}

/**
 * Keeps the files that are small enough to upload. A file over the limit is not sent at all:
 * the person is told which file is too big and what the limit is, instead of waiting for the server to refuse it.
 */
export function filterUploadableFiles(files: FileList | File[]): File[] {
  const all = Array.from(files);
  const tooBig = all.filter((f) => f.size > MAX_UPLOAD_BYTES);
  if (tooBig.length > 0) {
    const lines = tooBig.map((f) => `• ${f.name} (${formatFileSize(f.size)})`).join('\n');
    alert(
      tooBig.length === 1
        ? `"${tooBig[0].name}" is ${formatFileSize(tooBig[0].size)}. Files can be at most ${MAX_UPLOAD_MB} MB, so it was not uploaded. Please choose a smaller file or compress it.`
        : `These files are over the ${MAX_UPLOAD_MB} MB limit and were not uploaded:\n${lines}`
    );
  }
  return all.filter((f) => f.size <= MAX_UPLOAD_BYTES);
}
